import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';

import { CardCollectionTask } from '#src/components/missions/CardCollectionTask/index.tsx';
import { ClaimedTask } from '#src/components/missions/ClaimedTask/index.tsx';
import { CollectionCompletionTask } from '#src/components/missions/CollectionCompletionTask/index.tsx';
import { DecisivePlayerPickerTaskStatus } from '#src/components/missions/DailyTask/DecisivePlayerPickerTaskStatus/index.tsx';
import { DropChallengeButton } from '#src/components/missions/DailyTask/DropChallengeButton/index.tsx';
import { DailyTask } from '#src/components/missions/DailyTask/index.tsx';
import { GameWeeklyTask } from '#src/components/missions/GameWeeklyTask/index.tsx';
import { OnboardingTask } from '#src/components/missions/OnboardingTask/index.tsx';
import { StreakTask } from '#src/components/missions/StreakTask/index.tsx';
import { ThresholdsDuoTask } from '#src/components/missions/ThresholdsDuoTask/index.tsx';
import { UnclaimedTask } from '#src/components/missions/UnclaimedTask/index.tsx';
import { getTaskColors } from '#src/components/missions/getTaskColors.tsx';
import { hasEditableTaskAppearance } from '#src/components/missions/utils.ts';

import type { AnyTask_taskInterface } from './__generated__/index.graphql.ts';

type Props = {
  task: AnyTask_taskInterface;
  featured?: boolean;
  onDropChallengeClick?: () => void;
  onClaim?: () => void;
  loading?: boolean;
};
export const AnyTask = ({
  task,
  featured,
  onDropChallengeClick,
  onClaim,
  loading,
}: Props) => {
  const { borderColor } = getTaskColors(task, featured) || {};
  const isDailyActionTask = isTypeFn('DailyActionTask');

  if (
    task.aasmState === ManagerProgressionTaskState.CLAIMED &&
    !isDailyActionTask(task)
  ) {
    return <ClaimedTask claimedTask={task} />;
  }

  if (
    task.aasmState === ManagerProgressionTaskState.COMPLETED &&
    !hasEditableTaskAppearance(task) &&
    !isDailyActionTask(task)
  ) {
    return (
      <UnclaimedTask
        unclaimedTask={task}
        borderColor={borderColor}
        featured={featured}
        onClaim={onClaim}
        loading={loading}
      />
    );
  }
  if (isType(task, 'CardsCountTask')) {
    return <CardCollectionTask cardCollectionTask={task} featured={featured} />;
  }
  if (isType(task, 'CompleteCollectionsCountTask')) {
    return (
      <CollectionCompletionTask
        collectionCompletionTask={task}
        featured={featured}
      />
    );
  }
  if (isType(task, 'DropChallengeTask')) {
    return (
      <DailyTask
        dailyTask={task}
        featured={featured}
        borderColor={borderColor}
        action={
          <DropChallengeButton onClick={onDropChallengeClick} task={task} />
        }
      />
    );
  }
  if (isType(task, 'DecisivePlayerPickerTask')) {
    return (
      <DailyTask
        dailyTask={task}
        featured={featured}
        borderColor={borderColor}
        action={<DecisivePlayerPickerTaskStatus task={task} />}
      />
    );
  }
  if (isType(task, 'GameWeeklyTask')) {
    return (
      <GameWeeklyTask
        gameWeeklyTask={task}
        featured={featured}
        borderColor={borderColor}
      />
    );
  }
  if (isType(task, 'ThresholdsStreakTask')) {
    return <StreakTask streakTask={task} featured={featured} />;
  }
  if (isType(task, 'ThresholdsDuoConnectionTask')) {
    return <ThresholdsDuoTask task={task} />;
  }
  if (isType(task, 'Task')) {
    return <OnboardingTask onboardingTask={task} borderColor={borderColor} />;
  }
  // for unknown tasks, we don't want to show anything
  return null;
};

AnyTask.fragments = {
  taskInterface: gql`
    fragment AnyTask_taskInterface on TaskInterface {
      id
      aasmState
      ...UnclaimedTask_taskInterface
      ...CardCollectionTask_taskInterface
      ...CollectionCompletionTask_taskInterface
      ...DailyTask_taskInterface
      ...GameWeeklyTask_taskInterface
      ...StreakTask_taskInterface
      ...ThresholdsDuoTask_taskInterface
      ...OnboardingTask_taskInterface
      ...getTaskColors_taskInterface
      ...DecisivePlayerPickerTaskStatus_decisivePlayerPickerTask
      ...hasEditableTaskAppearance_taskInterface
    }
    ${UnclaimedTask.fragments.taskInterface}
    ${CardCollectionTask.fragments.taskInterface}
    ${CollectionCompletionTask.fragments.taskInterface}
    ${DailyTask.fragments.taskInterface}
    ${GameWeeklyTask.fragments.taskInterface}
    ${StreakTask.fragments.taskInterface}
    ${ThresholdsDuoTask.fragments.taskInterface}
    ${OnboardingTask.fragments.taskInterface}
    ${getTaskColors.fragments.taskInterface}
    ${DecisivePlayerPickerTaskStatus.fragments.decisivePlayerPickerTask}
    ${hasEditableTaskAppearance.fragments.taskInterface}
  ` as TypedDocumentNode<AnyTask_taskInterface>,
};
